import { Component, inject, OnInit, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";
import {
  TableActionConfig,
  TableColumn,
  TableConfig,
  TableDateRangeEvent,
  TableFilterChangeEvent,
  TablePagination,
  TableSearchEvent,
  TableSort,
} from "../../../../Common/Components/Table/Type/TableType";
import { AppTableComponent } from "../../../../Common/Components/Table/TableComponent";
import { ShipmentEntity } from "../../Dto/ShipmentModel";
import { ShipmentEffects } from "../../State/ShipmentEffects";
import { ShipmentState } from "../../State/ShipmentState";
import { formatDateNumeric } from "../../../../Common/Utility/UtilityDate";
import { ConfirmModalEffects } from "../../../../Common/Components/ConfirmModal/State/ConfirmModalEffects";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-shipment-home",
  standalone: true,
  imports: [AppTableComponent],
  templateUrl: "./ShipmentHomePage.html",
  styleUrl: "./ShipmentHomePage.scss",
  encapsulation: ViewEncapsulation.None,
})
export class ShipmentHomePage implements OnInit {
  private readonly router = inject(Router);
  private readonly shipmentEffects = inject(ShipmentEffects);
  private readonly confirmModalEffects = inject(ConfirmModalEffects);
  private readonly toastEffects = inject(ToastEffects);
  readonly shipmentState = inject(ShipmentState);

  selectedStatusFilter = "";
  readonly shipmentStatusOptions: { value: ShipmentEntity["status"]; label: string }[] = [
    { value: "Draft", label: "Setup / Packing" },
    { value: "Packed", label: "Ready to Dispatch" },
    { value: "Dispatched", label: "In Transit" },
    { value: "Delivered", label: "Delivered" },
    { value: "Cancelled", label: "Cancelled" },
  ];

  readonly shipmentColumns: TableColumn<ShipmentEntity>[] = [
    { key: "shipmentNo", header: "Shipment No", sortable: false, width: "15%" },
    {
      key: "destinationInstitutionName",
      header: "Destination",
      sortable: false,
      width: "18%",
      cellTemplate: (item) => [item.destinationInstitutionName || "-", item.routeSummary || ""].filter(Boolean).join(" · "),
    },
    {
      key: "recipientName",
      header: "Recipient",
      sortable: false,
      width: "15%",
      cellTemplate: (item) => item.recipientName || "-",
    },
    {
      key: "warehouse.name",
      header: "Warehouse",
      sortable: false,
      width: "15%",
      cellTemplate: (item) => item.warehouse?.name || "-",
    },
    {
      key: "status",
      header: "Status",
      sortable: false,
      width: "15%",
      cellTemplate: (item) => this.getStatusLabel(item.status),
      cellClass: (item) => this.getStatusChipClass(item.status),
      cellIcon: (item) => this.getStatusChipIcon(item.status),
    },
    {
      key: "boxCount",
      header: "Boxes",
      sortable: false,
      width: "8%",
      align: "center",
      cellTemplate: (item) => item.boxCount ?? 0,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: false,
      width: "12%",
      cellTemplate: (item) =>
        (() => {
          if (item.createdAt) {
            return formatDateNumeric(item.createdAt);
          }
          return "-";
        })(),
    },
    {
      key: "nextAction",
      header: "Next Action",
      sortable: false,
      width: "14%",
      cellTemplate: (item) => item.nextAction || "-",
    },
  ];

  get shipmentConfig(): TableConfig<ShipmentEntity> {
    return {
      loading: this.shipmentState.loading(),
      emptyMessage: this.shipmentState.error(),
      showActions: true,
      showSearch: true,
      searchPlaceholder: "Search shipments...",
      showCreate: true,
      createButtonLabel: "Create Shipment",
      createButtonIcon: "add",
      showDateRangeFilter: true,
      dateRangeFilterLabel: "Filter by creation date",
      filters: [
        {
          key: "status",
          label: "Status",
          placeholder: "All status",
          options: this.shipmentStatusOptions,
          defaultValue: this.selectedStatusFilter,
        },
      ],
      onCreate: () => this.createShipment(),
      actionButtons: [
        { label: "edit", icon: "edit", handler: (item) => this.editShipment(item) },
        {
          label: "delete",
          icon: "delete",
          handler: (item) => this.openDeleteShipmentModal(item),
        },
      ],
    };
  }

  get shipmentPagination(): TablePagination {
    return this.shipmentState.pagination();
  }

  ngOnInit(): void {
    const current = this.shipmentState.searchParams();
    this.selectedStatusFilter = current.status ?? "";
    this.shipmentEffects.searchShipments(current).catch(() => undefined);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSortChange(sort: TableSort | null): void {
    return;
  }

  onPageChange(event: { page: number; search?: string }): void {
    const current = this.shipmentState.searchParams();
    this.shipmentEffects.searchShipments({ ...current, page: event.page, search: event.search }).catch(() => undefined);
  }

  onPageSizeChange(event: { limit: number; search?: string }): void {
    const current = this.shipmentState.searchParams();
    this.shipmentEffects.searchShipments({ ...current, page: 1, limit: event.limit, search: event.search }).catch(() => undefined);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onActionClick<T>(event: { action: TableActionConfig<T>; item: T }): void {
    return;
  }

  onSearchChange(event: TableSearchEvent): void {
    const current = this.shipmentState.searchParams();
    this.shipmentEffects.searchShipments({ ...current, search: event.term, page: event.page }).catch(() => undefined);
  }

  onFilterChange(event: TableFilterChangeEvent): void {
    this.selectedStatusFilter = event.filters["status"] ?? "";
    const current = this.shipmentState.searchParams();
    this.shipmentEffects
      .searchShipments({
        ...current,
        status: (this.selectedStatusFilter || undefined) as ShipmentEntity["status"] | undefined,
        page: 1,
      })
      .catch(() => undefined);
  }

  onDateRangeChange(event: TableDateRangeEvent): void {
    const current = this.shipmentState.searchParams();
    this.shipmentEffects
      .searchShipments({
        ...current,
        page: event.page,
        createdAtFrom: event.dateRange.from,
        createdAtTo: event.dateRange.to,
      })
      .catch(() => undefined);
  }

  createShipment(): void {
    this.router.navigate(["/shipments/create"]).catch(() => undefined);
  }

  editShipment(item: ShipmentEntity): void {
    this.router.navigate(["/shipments/edit", item.id]).catch(() => undefined);
  }

  openDeleteShipmentModal(item: ShipmentEntity): void {
    this.confirmModalEffects.open({
      title: "Delete shipment?",
      message: `This will permanently delete shipment ${item.shipmentNo}.`,
      confirmText: "Delete shipment",
      cancelText: "Cancel",
      variant: "danger",
      closeOnOverlay: true,
      onConfirm: async () => {
        try {
          await this.shipmentEffects.deleteShipment(item.id);
          this.toastEffects.show("Shipment deleted.", "success");
        } catch (exception: unknown) {
          this.toastEffects.show(getBackendErrorMessage(exception, "Unable to delete shipment."), "error");
        }
      },
    });
  }

  private getStatusChipClass(status: ShipmentEntity["status"]): string {
    if (status === "Draft") return "app-table__chip app-table__chip--neutral";
    if (status === "Packed") return "app-table__chip app-table__chip--warning";
    if (status === "Dispatched") return "app-table__chip app-table__chip--info";
    if (status === "Delivered") return "app-table__chip app-table__chip--success";
    return "app-table__chip app-table__chip--danger";
  }

  private getStatusLabel(status: ShipmentEntity["status"]): string {
    return this.shipmentStatusOptions.find((option) => option.value === status)?.label ?? status;
  }

  private getStatusChipIcon(status: ShipmentEntity["status"]): string {
    if (status === "Draft") return "inventory";
    if (status === "Packed") return "inventory_2";
    if (status === "Dispatched") return "local_shipping";
    if (status === "Delivered") return "verified";
    return "cancel";
  }
}
